import { useCallback } from 'react'

import { toast } from '@webapp/components/Notification'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { useMutateCreatePOCustomField } from '@webapp/modules/purchasing/features/create-po-custom-field/models'
import { type CreatePOCustomFieldFormSchema } from '@webapp/modules/purchasing/features/create-po-custom-field/views/CreatePOCustomFieldForm'
import messages from './messages'

const MAX_PO_CUSTOM_FIELDS = 12

export interface UseCreatePOCustomFieldFormPresenterParams {
  existingFields: CustomField[]
  totalCount: number
  onSuccess: () => void
}

export const useCreatePOCustomFieldFormPresenter = ({
  existingFields,
  totalCount,
  onSuccess,
}: UseCreatePOCustomFieldFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreatePOCustomField()

  const handleSubmit = useCallback(
    async (values: CreatePOCustomFieldFormSchema) => {
      // Max-count guard — strict cap against the pagination count, fixing
      // the Angular `length > 12` off-by-one (BHV-010); no request
      if (totalCount >= MAX_PO_CUSTOM_FIELDS) {
        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: intl.formatMessage(messages.maxFieldsError),
        })
        return
      }

      // Case-insensitive uniqueness guard (BHV-011); no request
      const normalizedName = values.name.trim().toLowerCase()
      const isDuplicate = existingFields.some(
        (field) => field.name.trim().toLowerCase() === normalizedName
      )

      if (isDuplicate) {
        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: intl.formatMessage(messages.duplicateNameError),
        })
        return
      }

      try {
        await mutateAsync(values)

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage),
        })

        onSuccess()
      } catch (error) {
        const errorMessage =
          getMessageFromError(error) ||
          intl.formatMessage(messages.defaultErrorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [existingFields, intl, mutateAsync, onSuccess, totalCount]
  )

  return {
    states: {
      isSaving: isLoading,
    },
    handlers: {
      handleSubmit,
    },
  }
}
